import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import { constants } from "@qgiv/core-js";
import { SEO } from "@qgiv/core-donor";
import {
    getInitialTouched,
    getInitialValues,
    getValidationSchema,
} from "./formik";
import RegistrationBase from "../templates/RegistrationBase";
import ConnectedClassificationPageContent from "./ConnectedClassificationPageContent";
import ConnectedUpdateFCRegistration from "../RegistrationPage/ConnectedUpdateFCRegistration";

const ClassificationPage = ({
    baseUrl,
    classifications = [],
    currentPage = 0,
    dispatch,
    documentTitle,
    hasClassifications,
    hasParentRegistration,
    registrations = [],
    removeNewTeamFromTeams,
    replaceRegistrationsOnRegistration,
    savedValues = {},
    settings,
    updateCurrentPage,
    updateRegistrationClassification,
}) => {
    const {
        ENUMS: {
            DefaultView: { REGISTRATION_CLASSIFICATION_STEP },
        },
    } = constants;
    const [toRegistration, setToRegistration] = useState(false);

    useEffect(() => {
        if (currentPage !== REGISTRATION_CLASSIFICATION_STEP) {
            dispatch(updateCurrentPage(REGISTRATION_CLASSIFICATION_STEP));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    // Takes the user to the registration page from the classification page if
    // the form does not have any classifications
    useEffect(() => {
        if (!hasClassifications) {
            setToRegistration(true);
        }
    }, [hasClassifications]);

    const onSubmit = (values, actions) => {
        // TODO: Simulate some kind of processing state

        // we need to check the classification selected against registrations
        // to ensure that the data we are about to set is acceptable
        // Like outdated
        const selectedClassification =
            classifications.find(
                (currentClassification) =>
                    currentClassification.id ===
                    Number(values.Classification[0]),
            ) || {};

        const { teamSpotsLeft = 0 } = selectedClassification;

        if (teamSpotsLeft === 0) {
            let changedRegistrations = false;
            const newRegistrations = registrations.map(
                (currentRegistration) => {
                    const { Team = "" } = currentRegistration;
                    if (Team === "new-team") {
                        changedRegistrations = true;
                        return {
                            ...currentRegistration,
                            Team: "",
                        };
                    }
                    return currentRegistration;
                },
            );

            if (changedRegistrations) {
                dispatch(replaceRegistrationsOnRegistration(newRegistrations));
                // remove new team from teams
                dispatch(removeNewTeamFromTeams());
            }
        }

        setTimeout(() => {
            // 1. Store values in redux store via dispatching an action
            dispatch(updateRegistrationClassification(values));
            // 2. Set `submitting` to false
            actions.setSubmitting(false);
            // 3. Determine where to navigate user
            // Navigate to registration page
            window.scrollTo(0, 0);
            setToRegistration(true);
        }, 500);
    };

    // Redirect to the primary registrant's registration details

    const navigate = useNavigate();

    useEffect(() => {
        if (toRegistration) {
            navigate(`${baseUrl}/form/registration`, {
                state: { currentRegistration: 0 },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [toRegistration]);

    const formikProps = {
        initialTouched: getInitialTouched(settings, savedValues),
        initialValues: getInitialValues(settings, savedValues),
        onSubmit,
        validationSchema: getValidationSchema(),
    };

    return (
        <Formik {...formikProps}>
            <RegistrationBase>
                <SEO title={documentTitle} description="" />
                <Form>
                    {hasParentRegistration && <ConnectedUpdateFCRegistration />}
                    <ConnectedClassificationPageContent />
                </Form>
            </RegistrationBase>
        </Formik>
    );
};

ClassificationPage.propTypes = {
    baseUrl: PropTypes.string.isRequired,
    dispatch: PropTypes.func.isRequired,
    // Current page
    classifications: PropTypes.arrayOf(PropTypes.shape({})),
    currentPage: PropTypes.number,
    // String that contains the document title that appears in the browser tab
    documentTitle: PropTypes.string.isRequired,
    // Flag determining if registration form has classifications
    hasClassifications: PropTypes.bool.isRequired,
    // Flag for when to run UpdateFCRegistration
    hasParentRegistration: PropTypes.bool.isRequired,
    registrations: PropTypes.arrayOf(PropTypes.shape({})),
    removeNewTeamFromTeams: PropTypes.func.isRequired,
    replaceRegistrationsOnRegistration: PropTypes.func.isRequired,
    // Saved/previously selected values for classification page
    savedValues: PropTypes.shape({}),
    // Settings object with form and event settings
    settings: PropTypes.shape({}).isRequired,
    /**
     * @function updateRegistrationClassification
     * @param {object} payload Action payload
     * @param {string|number} classification The classification id to store
     * @description Redux action to update stored registration classification
     */
    updateRegistrationClassification: PropTypes.func.isRequired,
    // Redux action to update stored current page
    updateCurrentPage: PropTypes.func.isRequired,
};

export default ClassificationPage;
